<template>
  <div class="card payment-card">
    <div class="payment-form-header">
      <icon name="lock"></icon>
      <span>{{ $t('payment.cardEncrypt') }}</span>
    </div>
    <div class="padding-a-m">
      <h2 class="margin-a-z margin-b-s">{{ $t('rsvpEdit.paymentInfo')}}</h2>

      <form v-on:submit.prevent="submitPayment">
        <div v-if="showPaymentDropdown">
          <div :class="['field-group pad-b' + (validation.paymentMethod? '': ' field-error')]">
            <select class="field-text field-select" v-model="selectedPayment" v-on:change="onPaymentSelect">
              <option disabled value="-1">{{ $t('payment.selectCard') }}</option>
              <option value="directBill" v-if="isCorporate">{{ $t('payment.directBill') }}</option>
              <option v-for="(payment, i) in existingPayments" :value="payment.id" :key="i">
                {{ payment.number + ' (Exp: ' + payment.exp + ') - ' + payment.type }}
              </option>
              <option value="0">{{ $t('payment.useNew') }}</option>
            </select>
            <label class="field-error-message">{{ $t('payment.selectPayment') }}</label>
          </div>
        </div>

        <div ref="NewPaymentForm" class="row margin-t-s">
          <div v-if="useStripeForm" class="column">
            <stripe-payment-form v-show="!isPaymentFormHidden" ref="stripeForm" @failed="failedSubmitStripe"></stripe-payment-form>
          </div>
          <div v-else :class="['payment-form showhide-fadeslide' + (isPaymentFormHidden? ' hide': '')]">
            <p class="text-right margin-b-s margin-t-z">
              <small>{{ $t('payment.availablePayment') }}</small>
            </p>
            <div class="text-right">
              <div :class="['cc-icon inline visa', paymentMethods.visa]"></div>
              <div v-bind:class="['cc-icon inline mastercard', paymentMethods.mastercard]"></div>
              <div :class="['cc-icon inline amex',+ paymentMethods.amex]"></div>
              <div :class="['cc-icon inline discover ' + paymentMethods.discover]"></div>
              <div :class="['cc-icon inline jcb', paymentMethods.jcb]"></div>
              <div :class="['cc-icon inline dinersclub', paymentMethods.dinersclub]"></div>
            </div>
            <div class="row">
              <div class="medium-12 columns">
                <div v-bind:class="['field-group' + (validation.cardName? '': ' field-error')]">
                  <label class="field-label">{{ $t('payment.nameOnCard') }}</label>
                  <input class="field-text" type="text" v-model:bind="card.name" placeholder="i.e. John Appleseed" />
                  <label class="field-error-message">
                    {{ $t('payment.isRequired') }}
                  </label>
                </div>
              </div>
            </div>

            <div class="row">
              <div class="medium-12 columns">
                <div v-bind:class="['field-group' + (validation.cardNumber? '': ' field-error')]">
                  <label class="field-label">{{ $t('payment.cardNumber') }}</label>
                  <input class="field-text" type="text" v-model:bind="card.number"
                    v-on:keyup="updateIcon" v-on:keypress="isNumber" />
                  <label class="field-error-message">
                    {{ $t('payment.isRequired') }}
                  </label>
                </div>
              </div>
            </div>

            <div class="row">
              <div class="small-6 columns padding-r-s">
                <div v-bind:class="['field-group' + (validation.cardExp? '': ' field-error')]">
                  <label class="field-label">{{ $t('payment.exp') }} (MM/YYYY)</label>
                  <input class="field-text" type="text" v-model:bind="card.exp" 
                    placeholder="MM/YYYY" v-on:keyup="formatDate" v-on:keypress="isNumber" />
                  <label class="field-error-message">
                    {{ validationMsg.cardExp }}
                  </label>
                </div>
              </div>

              <div class="small-6 columns padding-l-s">
                <div v-bind:class="['cvv-field field-group' + (validation.cardCvv? '': ' field-error')]">
                  <label class="field-label">
                    <span>{{ $t('payment.securityCode') }}</span>
                  </label>
                  <input class="field-text" type="text" v-model:bind="card.cvv"
                    v-on:keypress="isNumber" />
                  <label class="field-error-message">
                    {{ $t('payment.isRequired') }}
                  </label>
                  <a class="cvv-hint" href="javascript:;"
                    @click="toggleCvvInfo"
                    v-click-outside="hideCvvInfo">?</a>
                </div>
              </div>
            </div>

            <div class="row">
              <div class="small-12 medium-6 medium-offset-6 columns">
                <div v-bind:class="['cvv-field' + (showCvvInfo? '': ' hide')]">
                  <div class="cvv-info margin-l-s small-margin-l-z padding-a-m">
                    <p class="margin-t-z margin-b-s">
                      {{ $t('payment.for') }} 
                      <strong>Visa, MasterCard, JCB, Diners Club &amp; Discover</strong>,
                      {{ $t('payment.digitCard1') }}
                    </p>
                    <p class="margin-t-z margin-b-s">
                      {{ $t('payment.for') }} 
                      <strong>American Express</strong>, 
                      {{ $t('payment.digitCard2') }}
                    </p>
                    <div class="row">
                      <div class="small-6 columns padding-r-s">
                        <img src="../../assets/img/non-amex-hint.png" />
                      </div>
                      <div class="small-6 columns padding-l-s">
                        <img src="../../assets/img/amex-hint.png" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="row">
              <div class="medium-8 columns padding-r-s small-padding-h-z">
                <div v-bind:class="['field-group' + (validation.cardAddress? '': ' field-error')]">
                  <label class="field-label">{{ $t('payment.billingAddr') }}</label>
                  <input class="field-text" type="text" v-model:bind="card.billingAddress" />
                  <label class="field-error-message">
                    {{ $t('payment.isRequired') }}
                  </label>
                </div>
              </div>
              <div class="small-6 medium-4 columns padding-l-s small-padding-l-z small-padding-r-s">
                <div v-bind:class="['field-group' + (validation.cardCity? '': ' field-error')]">
                  <label class="field-label">{{ $t('payment.city') }}</label>
                  <input class="field-text" type="text" v-model:bind="card.billingCity" />
                  <label class="field-error-message">
                    {{ $t('payment.isRequired') }}
                  </label>
                </div>
              </div>

              <div class="small-6 medium-4 columns padding-r-s small-padding-r-z small-padding-l-s">
                <div v-bind:class="['field-group' + (validation.cardState? '': ' field-error')]">
                  <label class="field-label">{{ $t('payment.state') }}</label>
                  <select class="field-select field-text" v-model="card.billingState" v-on:change="onStateSelect">
                    <option disabled value="-1">{{ $t('payment.chooseOne') }}</option>
                    <option v-for="state in states" v-bind:value="state.value">{{ state.name }}</option>
                  </select>
                  <label class="field-error-message">
                    {{ $t('payment.isRequired') }}
                  </label>
                </div>
              </div>
              <div :class="[showCountry?
                'small-12 medium-4 columns padding-l-s padding-r-s small-padding-a-n':
                'small-12 medium-8 columns padding-l-s small-padding-a-n']">
                <div v-bind:class="['field-group' + (validation.cardZip? '': ' field-error')]">
                  <label class="field-label">{{ $t('payment.zip') }}</label>
                  <input class="field-text" type="text"
                    v-model:bind="card.billingZip" @keypress="isMax20"/>
                  <label class="field-error-message">
                    {{ $t('payment.isRequired') }}
                  </label>
                </div>
              </div>
              <div v-bind:class="['small-12 medium-4 columns padding-l-s small-padding-a-n' + (showCountry? '': ' hide')]">
                <div v-bind:class="['field-group' + (validation.cardCountry? '': ' field-error')]">
                  <label class="field-label">{{ $t('payment.country') }}</label>
                  <select class="field-select field-text" v-model="card.billingCountry">
                    <option disabled value="-1">{{ $t('payment.chooseOne') }}</option>
                    <option v-for="(country, x) in countries" v-bind:value="country.value" :key="x">
                      {{ country.name }}
                    </option>
                  </select>
                  <label class="field-error-message">
                    {{ $t('payment.isRequired') }}
                  </label>
                </div>
              </div>
            </div>
          </div>

          <div class="clear"></div>

          <div v-bind:class="['alert-wrap showhide-fadeslide' + (showCreditCardError ? '':' hide')]">
            <div class="small-12 columns">
              <div class="alert error margin-t-m">
                {{ creditCardError }}
              </div>
            </div>
          </div>

          <div class="d-flex justify-space-between column">
            <div class="page-actions-wrap">
              <a href="#/customer" class="op-btn btn-lg btnBack" @click="back($event)">
                <icon name="chevron-left" class="margin-r-xs"></icon>
                {{ $t('common.back') }}
              </a>
              <label class="margin-t-xs">
                <span>{{ $t('payment.previous') }}:</span>
                {{ $t('payment.yourInfo') }}
              </label>
            </div>
            <div class="page-actions-wrap text-right">
              <a href="#/review" :class="['op-btn btn-success btn-lg btnContinue', {loading:isSubmittingForm}]"
                @click="submitForm($event)">
                <div class="original-label">
                  {{ $t('common.continue') }}
                  <icon name="chevron-right" class="margin-l-xs"></icon>
                </div>
                <div class="loaderx">
                  <icon name="refresh" spin class="magin-r-xs"></icon>
                  {{ $t('payment.validating') }}
                </div>
              </a>
              <button type="submit" ref="submitBtn" v-show="false"></button>
              <label class="margin-t-xs">
                <span>{{ $t('common.next') }}:</span>
                {{ $t('payment.reviewBook') }}
              </label>
            </div>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import VueCookie from 'vue-cookie'
import US from 'us'
import Countries from '../../data/countries.js'
import creditCardType, { types as CardType } from 'credit-card-type'
import StripePaymentForm from './StripePaymentForm.vue'

Vue.use(VueCookie);

export default {
  name: 'payment-form',
  props: [ 'user', 'flash', 'cards' ],
  directives: {
    'click-outside': {
      bind (el, binding, vNode) {
        // Provided expression must evaluate to a function.
        if (typeof binding.value !== 'function') {
          const compName = vNode.context.name
          let warn = `[Vue-click-outside:] provided expression '${binding.expression}' is not a function, but has to be`
          if (compName) { warn += `Found in component '${compName}'` }

          console.warn(warn)
        }
        // Define Handler and cache it on the element
        const bubble = binding.modifiers.bubble
        const handler = (e) => {
          if (bubble || (!el.contains(e.target) && el !== e.target)) {
            binding.value(e)
          }
        }
        el.__vueClickOutside__ = handler

        // add Event Listeners
        document.addEventListener('click', handler)
      },

      unbind (el, binding) {
        // Remove Event Listeners
        document.removeEventListener('click', el.__vueClickOutside__)
        el.__vueClickOutside__ = null
      }
    }
  },
  components: { StripePaymentForm },
  data () {
    return {
      title: 'Payment Information',
      showPaymentDropdown: true,
      isPaymentFormHidden: true,
      isSubmittingForm: false,
      showCountry: false,
      creditCardError: '',
      showCreditCardError: false,
      showCvvInfo: false,
      validation: {
        paymentMethod: true,
        cardName: true,
        cardNumber: true,
        cardExp: true,
        cardCvv: true,
        cardAddress: true,
        cardCity: true,
        cardState: true,
        cardZip: true,
        cardCountry: true
      },
      validationMsg: {
        cardExp: this.$t('payment.isRequired')
      },
      card: {
        title: '',
        name: '',
        number: '',
        exp: '',
        cvv: '',
        billingAddress: '',
        billingCity: '',
        billingState: '-1',
        billingZip: '',
        billingCountry: '-1'
      },
      paymentMethods: {
        visa: '',
        mastercard: '',
        amex: '',
        discover: '',
        jcb: '',
        dinersclub: ''
      },
      existingPayments: [],
      selectedPayment: this.$store.state.selectedCard.id,
      useStripeForm: true
    }
  },
  computed: {
    states () {
      let result = []

      result.push({
        name: 'Non US',
        value: ''
      })

      let states = US.STATES
      for (let i = 0; i < states.length; i++) {
        result.push({
          name: states[i].name,
          value: states[i].abbr
        })
      }

      return result
    },
    countries () {
      let result = []
      for (let property in Countries) {
        if (!Countries.hasOwnProperty(property)) {
          continue
        }

        if (property === 'us') {
          continue
        }

        result.push({
          name: Countries[property].name,
          value: property.toUpperCase()
        })
      }

      return result
    },
    userStore () { return this.$store.state.user },
    isCorporate () { return this.userStore.isCorporate },
    d2d () { return this.$store.state.reservation.d2d },
    bookingType () { return this.$store.state.reservation.bookingType }
  },
  methods: {
    clearValidation () {
      this.validation.paymentMethod = true
      this.validation.cardName = true
      this.validation.cardNumber = true
      this.validation.cardExp = true
      this.validation.cardCvv = true
      this.validation.cardAddress = true
      this.validation.cardCity = true
      this.validation.cardState = true
      this.validation.cardZip = true
      this.validation.cardCountry = true
    },
    validatePaymentForm () {
      let self = this
      let card = self.card
      let result = true;

      self.clearValidation()

      if (card.name === '') {
        self.validation.cardName = false
        result = false
      }

      let cardInfo = creditCardType(card.number);
      if (cardInfo.length !== 1) {
        self.validation.cardNumber = false
        result = false
        card.title = '';
      } else {
        card.title = cardInfo[0].niceType;
      }

      // expiration validation
      if (card.exp === '') {
        self.validation.cardExp = false
        self.validationMsg.cardExp = self.$t('payment.isRequired')
        result = false
      }

      if (card.exp !== '') {
        // check format
        let expTokens = card.exp.split('/')
        if (expTokens.length < 2 ||
            expTokens[0].length !== 2 ||
            expTokens[1].length !== 4) {
          self.validation.cardExp = false
          self.validationMsg.cardExp = `${self.$t('payment.followFormat')} MM/YYYY.`
          result = false
        }

        let expMonth = Number(expTokens[0])
        if (expMonth < 1 || expMonth > 12) {
          self.validation.cardExp = false
          self.validationMsg.cardExp = self.$t('payment.invalidExp')
          result = false
        }

        let d = new Date()
        let currentYear = d.getFullYear()
        let expYear = Number(expTokens[1])
        if (expYear < currentYear) {
          self.validation.cardExp = false
          self.validationMsg.cardExp = self.$t('payment.futureDate')
          result = false
        }

        let currentMonth = d.getMonth() + 1;
        if (expYear === currentYear && expMonth < currentMonth) {
          self.validation.cardExp = false
          self.validationMsg.cardExp = self.$t('payment.futureDate')
          result = false
        }
      }
      // expiration end

      if (card.cvv === '') {
        self.validation.cardCvv = false
        result = false
      }

      if (card.billingAddress === '') {
        self.validation.cardAddress = false
        result = false
      }

      if (card.billingCity === '') {
        self.validation.cardCity = false
        result = false
      }

      if (card.billingState === '-1') {
        self.validation.cardState = false
        result = false
      }

      /*
      if (card.billingZip === '') {
        self.validation.cardZip = false
        result = false
      }
      */

      if (card.billingState === '' && card.billingCountry === '-1') {
        self.validation.cardCountry = false
        result = false
      }

      return result
    },
    submitPayment () {
      this.clearValidation()
      this.isSubmittingForm = true;
      if (this.selectedPayment === -1) {
        this.validation.paymentMethod = false
        this.isSubmittingForm = false;
        return;
      }

      if (this.selectedPayment === '0' || this.selectedPayment === 0) {
        if (this.useStripeForm) {
          if (this.$refs.stripeForm.submitPayment()) {
            this.$refs.stripeForm.submitPayment()
          }
          return
        }
        let isValid = this.validatePaymentForm()

        if (!isValid) {
          this.isSubmittingForm = false;
          return
        }

        this.$root.$emit('addCard', {
          title: this.card.title,
          number: this.card.number,
          expiration: this.card.exp,
          name: this.card.name,
          cvv: this.card.cvv,
          billingAddress: this.card.billingAddress,
          billingCity: this.card.billingCity,
          billingState: this.card.billingState,
          billingCountry: this.card.billingCountry,
          billingZip: this.card.billingZip,
          token: this.$store.state.user.token
        })

        return
      }

      this.isSubmittingForm = false;
      let selectedCard = {};
      for (let i = 0; i < this.existingPayments.length; i++) {
        if (this.existingPayments[i].id === this.selectedPayment) {
          selectedCard = this.existingPayments[i]
          break;
        }
      }
      this.$store.commit('setSelectedCard', selectedCard)
      this.trackPaymentData()
      if (this.bookingType === 'flight') {
        this.$root.$router.push('review-summary')
      } else {
        this.$root.$router.push('review')
      }
    },
    onPaymentSelect () {
      this.clearValidation()
      if (this.selectedPayment === '0') {
        this.isPaymentFormHidden = false
        this.clearValidation()
        this.$scrollTo(this.$refs.NewPaymentForm)
      } else {
        this.isPaymentFormHidden = true
      }
      this.$store.commit('user/setIsDirectBilling', this.selectedPayment === 'directBill')

      if (window.gtag) { // eslint-disable-line
        gtag( // eslint-disable-line
          'event',
          'Payment_Method',
          {value: this.selectedPayment === '0' ? 'New Credit Card' : 'Use Existing Card'}
        )
      }
    },
    onStateSelect () {
      this.showCountry = (this.card.billingState === '')
      if (this.showCountry) {
        this.card.billingCountry = '-1'
      }
    },
    toggleCvvInfo () {
      this.showCvvInfo = true
    },
    hideCvvInfo () {
      this.showCvvInfo = false
    },
    disableCards () {
      this.paymentMethods.visa = 'disabled';
      this.paymentMethods.mastercard = 'disabled';
      this.paymentMethods.amex = 'disabled';
      this.paymentMethods.discover = 'disabled';
      this.paymentMethods.jcb = 'disabled';
      this.paymentMethods.dinersclub = 'disabled';
    },
    enableCards () {
      this.paymentMethods.visa = '';
      this.paymentMethods.mastercard = '';
      this.paymentMethods.amex = '';
      this.paymentMethods.discover = '';
      this.paymentMethods.jcb = '';
      this.paymentMethods.dinersclub = '';
    },
    updateIcon () {
      let self = this;
      let cardNumber = self.card.number;
      let length = cardNumber.length;

      if (length === 0) {
        self.enableCards();
      } else {
        self.disableCards();
      }

      let possibleCards = creditCardType(cardNumber);

      for (let i = 0; i < possibleCards.length; i++) {
        let possibleCard = possibleCards[i];
        let cardType = possibleCard.type;

        if (cardType === CardType.VISA) {
          self.paymentMethods.visa = '';
        } else if (cardType === CardType.MASTERCARD) {
          self.paymentMethods.mastercard = '';
        } else if (cardType === CardType.AMERICAN_EXPRESS) {
          self.paymentMethods.amex = '';
        } else if (cardType === CardType.DINERS_CLUB) {
          self.paymentMethods.dinersclub = '';
        } else if (cardType === CardType.DISCOVER) {
          self.paymentMethods.discover = '';
        } else if (cardType === CardType.JCB) {
          self.paymentMethods.jcb = '';
        }
      }
    },
    isNumber (evt) {
      let charCode = (evt.which) ? evt.which : evt.keyCode;
      if ((charCode > 31 && (charCode < 48 || charCode > 57))) {
        evt.preventDefault()
        return
      }
      return true;
    },
    isMax20 (evt) {
      if (this.card.billingZip.length >= 20) {
        evt.preventDefault()
        return
      }
      return true
    },
    formatDate () {
      let cardExp = this.card.exp
      if (cardExp.length === 2 && !isNaN(cardExp)) {
        this.card.exp = cardExp + '/'
      }
    },
    togglePaymentForm () {
      this.isPaymentFormHidden = !this.isPaymentFormHidden
    },
    parseCardClass (card) {
      let result = ''
      if (card.title === 'Visa') {
        result = 'visa'
      } else if (card.title === 'MasterCard' || card.title === 'Master Card') {
        result = 'mastercard'
      } else if (card.title === 'American Express') {
        result = 'amex'
      } else if (card.title === 'JCB') {
        result = 'jcb'
      } else if (card.title === 'Diners Club') {
        result = 'dinersclub'
      } else if (card.title === 'Discover') {
        result = 'discover'
      }
      return result
    },
    back (event) {
      if (event) event.preventDefault()

      if (this.bookingType === 'flight') {
        this.$root.$router.push('pax-info')
      } else {
        this.$root.$router.push('customer')
      }
      // window.location.href = '#/customer'
    },
    submitForm (event) {
      if (event) event.preventDefault()
      if (this.$refs.submitBtn) {
        this.$refs.submitBtn.click()
      }
    },
    trackPaymentData () {
      // if (window.opoliCNS) {
      //   let rsvpData = this.$store.state.reservation
      //   let leg1 = rsvpData.leg1
      //   let leg2 = rsvpData.leg2
      //   let segment = {
      //     key: 'Payment Page',
      //     'Leg 1': leg1.selectedService ? leg1.services[leg1.selectedService].prices[leg1.passenger + 1].price : '',
      //     'Leg 2': rsvpData.isRoundtrip && leg2.selectedService ? leg2.services[leg2.selectedService].prices[leg2.passenger + 1].price : '',
      //     'Total Price': '',
      //     'Pick Up Location': leg1.pickup.title,
      //     'Drop off Location': leg1.dropoff.title,
      //     'Flight Date': leg1.flightDate,
      //     'Pick Up Airline Info': `${leg1.airline} ${leg1.flightNo}`,
      //     'Service Type': leg1.selectedService ? leg1.services[leg1.selectedService].brandedName : '',
      //     'Pickup Type': leg1.pickupType,
      //     'Pickup Date': leg1.pickupDate,
      //     'Payment Payment Page_Abandonment': 'No',
      //     'Payment Page_Continue': 'Yes'
      //   }
      //   if (leg1.isAsap) {
      //     segment['Pickup Date'] = 'ASAP'
      //   }
      //   this.$store.commit('tracking/setOpoliCnsData', {
      //     // getTotal2: 'Payment Page'
      //     getTotal2: {
      //       key: 'Payment Page',
      //       segment: segment,
      //       url: null,
      //       widgetEvent: true
      //     }
      //   })
      // }
    },
    failedSubmitStripe () {
      this.isSubmittingForm = false
    }
  },
  created () {
  },
  mounted () {
    if (this.bookingType === 'flight') {
      if (!this.$store.state.user.token) {
        this.$root.$router.push('pax-info')
        return
      }
    } else {
      if (this.userStore && !this.userStore.id) {
        this.$root.$router.push('customer')
        return
      }
    }

    this.$store.commit('setCurrentIndex', 2)
    this.$store.commit('enablePreviousSteps', 2)

    this.$store.commit('setShowPaymentBreakdown', true)
    this.$store.commit('setShowPromoCodeInput', true)

    this.showPaymentDropdown = true
    if (!this.$store.state.cards.list.length) {
      this.showPaymentDropdown = false
      this.isPaymentFormHidden = false
      this.selectedPayment = '0'
    } else {
      this.existingPayments = this.$store.state.cards.list
    }

    let self = this
    this.$root.$on('getCardsSuccess', function (cards) {
      self.existingPayments = []

      if (typeof cards === 'undefined' || cards.length === 0) {
        self.showPaymentDropdown = false
        self.isPaymentFormHidden = false
        self.selectedPayment = '0';
      } else {
        for (let i = 0; i < cards.length; i++) {
          let card = cards[i]
          let className = self.parseCardClass(card)

          self.existingPayments.push({
            id: card.ID,
            type: card.title,
            number: card.cardNumber,
            exp: card.cardExpires,
            className: className
          })
        }
        self.showPaymentDropdown = true
        self.$store.commit('cards/setCardData', {
          lastUserID: self.$store.state.user.id,
          list: self.existingPayments
        })
      }
    })

    this.$root.$on('getCardsFail', function (cards) {
      self.showPaymentDropdown = false
      self.isPaymentFormHidden = false
      self.selectedPayment = '0'
    })

    this.$root.$on('addCardSuccess', function (response) {
      self.showCreditCardError = false
      self.isSubmittingForm = false

      let card = response.data.card
      let className = self.parseCardClass(card)
      let selectedCard = {
        id: card.ID,
        type: card.title,
        number: card.cardNumber,
        exp: card.cardExpires,
        className: className
      }

      self.$store.commit('setSelectedCard', selectedCard)
      self.trackPaymentData()
      if (self.bookingType === 'flight') {
        self.$root.$router.push('review-summary')
      } else {
        self.$root.$router.push('review')
      }
      if (/*CONFIG.Env == 'production' &&*/ window.gtag) { // eslint-disable-line
        gtag('event', 'Payment Continue') // eslint-disable-line
      }
    })

    this.$root.$on('addCardFail', function (response) {
      self.isSubmittingForm = false
      self.showCreditCardError = true

      let errorMsg = response.message
      if (typeof response.message === 'object') {
        errorMsg = response.message.message
      }
      self.creditCardError = errorMsg
    })

    // if (this.$ga) {
    //   this.$ga.page({
    //     page: '/payment',
    //     title: 'Payment Page',
    //     screenName: 'paymentPage'
    //   })
    // }
    // if (window.gtag) { // eslint-disable-line
    //   gtag('event', 'page_view', { // eslint-disable-line
    //     'page': 'Payment page'
    //   })
    // }

    let elmnt = document.getElementById('app')
    elmnt.scrollIntoView({behavior: 'smooth', block: 'start'})
  }
}
</script>

<style lang="scss" scoped>
@import "../../assets/css/CreditCardIcons.css";
@import "../../assets/scss/CreditCardIcons.scss";
@import "../../assets/scss/Payment/PaymentForm.scss";
h2{ font-size: 1.5em!important; }
.fa-icon {
  width: auto;
  height: 1em; /* or any other relative font sizes */
}
.op-btn, .op-btn:not(.loading) .original-label{ display: inline-flex!important; } /* cross browser fix */

.payment-form{
  padding: 0;
}
.btnContinue, .btnBack{
  text-decoration: none!important;
}
.pad-b{
  padding-bottom: 30px;
}
</style>



// WEBPACK FOOTER //
// PaymentForm.vue?2c5ae803